import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

async function authHeader(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() || '';
}

// ------------------------------------------------------------------
// Create Form
// ------------------------------------------------------------------

export interface CreateFormRequest {
  month: string;   // "YYYY-MM"
  type: 'comics' | 'staff';
}

export interface CreateFormResult {
  form_id: string;
  responder_url: string;
  edit_url: string;
  month: string;
  type: string;
}

export async function createAvailsForm(req: CreateFormRequest): Promise<CreateFormResult> {
  const token = await authHeader();
  const response = await api.post('/avails/create-form', req, {
    headers: { Authorization: token },
  });
  return response.data;
}

// ------------------------------------------------------------------
// Send Email
// ------------------------------------------------------------------

export interface SendEmailRequest {
  form_url: string;
  form_id?: string;
  month: string;
  type: 'comics' | 'staff';
  reminder: boolean;
}

export interface ValidationFailure {
  error: string;
  issues: string[];
}

export interface SendEmailResult {
  sent: number;
  failed: number;
  failed_emails: string[];
  skipped_already_submitted: number;
}

export async function sendAvailsEmail(req: SendEmailRequest): Promise<SendEmailResult> {
  const token = await authHeader();
  const response = await api.post('/avails/send-email', req, {
    headers: { Authorization: token },
  });
  return response.data;
}

// ------------------------------------------------------------------
// Ingest
// ------------------------------------------------------------------

export interface IngestRequest {
  avails_sheet_id: string;
  clear_existing: boolean;
  type: 'comics' | 'staff';
}

export interface StaffBreakdownRow {
  email: string;
  name: string;
  avails: number;
}

export interface IngestResult {
  success: boolean;
  message: string;
  statistics: {
    shows_count?: number;
    dates_count?: number;
    total_avails: number;
    ingested: number;
    skipped_dnb?: number;
    not_in_database?: number;
    errors: number;
  };
  staff_breakdown?: StaffBreakdownRow[];
  comics_not_in_db?: string[];
}

export async function ingestAvails(req: IngestRequest): Promise<IngestResult> {
  const token = await authHeader();
  const endpoint = req.type === 'staff' ? '/staff-avails/ingest' : '/avails/ingest';
  const payload = req.type === 'staff'
    ? { avails_sheet_id: req.avails_sheet_id, clear_existing: req.clear_existing }
    : { avails_sheet_id: req.avails_sheet_id, clear_existing: req.clear_existing };
  const response = await api.post(endpoint, payload, {
    headers: { Authorization: token },
    timeout: 130000,  // 130s — Lambda can run up to 120s
  });
  return response.data;
}
